import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';

export interface VisitorGeoInfo {
  ip: string | null;
  countryCode: string | null;
}

interface IpGeoProvider {
  url: string;
  responseType?: 'json' | 'text';
  parse: (res: unknown) => VisitorGeoInfo | null;
}

/**
 * Resolves the visitor's public IP and country (ISO 3166-1 alpha-2) via IP geolocation.
 * Used by the public /home guard and homepage content to apply per-country rules.
 *
 * Does not use browser locale, timezone, or logged-in profile — only the visitor IP.
 * Uses HttpBackend so geo-lookup requests bypass app HTTP interceptors
 * (withCredentials / error toasts would otherwise break third-party CORS).
 * Result is cached for the browser session so the public homepage only geo-lookups once.
 */
@Injectable({
  providedIn: 'root',
})
export class VisitorCountryService {
  private static readonly COUNTRY_CACHE_KEY = 'visitor_country_code';
  private static readonly IP_CACHE_KEY = 'visitor_ip';

  /** Bypasses JwtInterceptor, ErrorInterceptor, SpinnerInterceptor. */
  private readonly http: HttpClient;
  private cachedInfo: VisitorGeoInfo | null = null;
  private detectInFlight: Observable<VisitorGeoInfo> | null = null;

  private readonly providers: IpGeoProvider[] = [
    {
      url: 'https://api.country.is/',
      parse: (res) => this.parseSimpleCountryResponse(res),
    },
    {
      url: 'https://ipwho.is/',
      parse: (res) => this.parseIpwhoResponse(res),
    },
    {
      url: 'https://ipinfo.io/json',
      parse: (res) => this.parseSimpleCountryResponse(res),
    },
    {
      url: 'https://get.geojs.io/v1/ip/country.json',
      parse: (res) => this.parseSimpleCountryResponse(res),
    },
    {
      url: 'https://ipapi.co/json/',
      parse: (res) => this.parseIpapiResponse(res),
    },
    {
      url: 'https://ipapi.co/country_code/',
      responseType: 'text',
      parse: (res) => {
        const countryCode = String(res ?? '').trim().toUpperCase();
        return /^[A-Z]{2}$/.test(countryCode) ? { ip: null, countryCode } : null;
      },
    },
  ];

  constructor(httpBackend: HttpBackend) {
    this.http = new HttpClient(httpBackend);
    this.cachedInfo = this.loadCachedInfo();
  }

  /** Cached alpha-2 country code, if already detected this session. */
  getCachedCountryCode(): string | null {
    return this.cachedInfo?.countryCode ?? null;
  }

  /** Cached public IP, if already detected this session. */
  getCachedIp(): string | null {
    return this.cachedInfo?.ip ?? null;
  }

  /**
   * Detect country from the visitor IP. Returns null on failure
   * so callers can fall back to the general layout.
   */
  getCountryCode(): Observable<string | null> {
    return this.detect().pipe(map((info) => info.countryCode));
  }

  /** Public IP of the visitor (public homepage only). */
  getVisitorIp(): Observable<string | null> {
    return this.detect().pipe(map((info) => info.ip));
  }

  /** IP + country in one cached lookup. */
  detect(): Observable<VisitorGeoInfo> {
    if (this.cachedInfo?.countryCode) {
      return of(this.cachedInfo);
    }
    if (this.detectInFlight) {
      return this.detectInFlight;
    }

    this.detectInFlight = this.fetchFromProviders().pipe(
      tap((info) => {
        if (!info.countryCode) {
          console.warn('[VisitorCountry] Geo lookup failed — using general layout');
        } else {
          console.info('[VisitorCountry] Detected', {
            ip: info.ip,
            countryCode: info.countryCode,
          });
        }
        this.storeInfo(info);
      }),
      catchError((err) => {
        console.warn('[VisitorCountry] Geo lookup error', err);
        return of({ ip: null, countryCode: null });
      }),
      finalize(() => {
        this.detectInFlight = null;
      }),
      shareReplay(1),
    );

    return this.detectInFlight;
  }

  clearCache(): void {
    this.cachedInfo = null;
    this.detectInFlight = null;
    try {
      sessionStorage.removeItem(VisitorCountryService.COUNTRY_CACHE_KEY);
      sessionStorage.removeItem(VisitorCountryService.IP_CACHE_KEY);
    } catch {
      /* ignore */
    }
  }

  private fetchFromProviders(): Observable<VisitorGeoInfo> {
    return this.tryProvider(0);
  }

  private tryProvider(index: number): Observable<VisitorGeoInfo> {
    if (index >= this.providers.length) {
      return of({ ip: null, countryCode: null });
    }

    const provider = this.providers[index];
    const headers = new HttpHeaders({
      Accept: provider.responseType === 'text' ? 'text/plain' : 'application/json',
    });
    const request$ = provider.responseType === 'text'
      ? this.http.get(provider.url, { headers, responseType: 'text' })
      : this.http.get<unknown>(provider.url, { headers });

    return request$.pipe(
      switchMap((res) => {
        const info = provider.parse(res);
        if (info?.countryCode) {
          console.info('[VisitorCountry] Resolved via', provider.url, info);
          return of(info);
        }
        console.warn('[VisitorCountry] No country from', provider.url, res);
        return this.tryProvider(index + 1);
      }),
      catchError((err) => {
        console.warn('[VisitorCountry] Request failed for', provider.url, err?.status ?? err);
        return this.tryProvider(index + 1);
      }),
    );
  }

  private parseSimpleCountryResponse(res: unknown): VisitorGeoInfo | null {
    const body = this.asRecord(res);
    const countryCode = this.normalizeCountryCode(body);
    if (!countryCode) {
      return null;
    }
    const ip =
      typeof body?.['ip'] === 'string'
        ? body['ip'].trim()
        : typeof body?.['query'] === 'string'
          ? body['query'].trim()
          : null;
    return { ip: ip || null, countryCode };
  }

  private parseIpapiResponse(res: unknown): VisitorGeoInfo | null {
    const body = this.asRecord(res);
    const countryCode = this.normalizeCountryCode(body);
    if (!countryCode) {
      return null;
    }
    const ip = typeof body?.['ip'] === 'string' ? body['ip'].trim() : null;
    return { ip: ip || null, countryCode };
  }

  private parseIpwhoResponse(res: unknown): VisitorGeoInfo | null {
    const body = this.asRecord(res);
    if (body?.['success'] === false) {
      return null;
    }
    const countryCode = this.normalizeCountryCode(body);
    if (!countryCode) {
      return null;
    }
    const ip = typeof body?.['ip'] === 'string' ? body['ip'].trim() : null;
    return { ip: ip || null, countryCode };
  }

  private asRecord(res: unknown): Record<string, unknown> | null {
    const value = Array.isArray(res) ? res.find((item) => item && typeof item === 'object') : res;
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }
    return value as Record<string, unknown>;
  }

  private normalizeCountryCode(res: Record<string, unknown> | null | undefined): string | null {
    const raw =
      res?.['country_code'] ??
      res?.['countryCode'] ??
      res?.['country'];
    if (!raw || typeof raw !== 'string') {
      return null;
    }
    const code = raw.trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(code)) {
      return code;
    }
    // Ignore full country names (e.g. "United States") — only ISO alpha-2 is valid.
    return null;
  }

  private loadCachedInfo(): VisitorGeoInfo | null {
    try {
      const countryCode = sessionStorage
        .getItem(VisitorCountryService.COUNTRY_CACHE_KEY)
        ?.trim()
        .toUpperCase();
      const ip = sessionStorage.getItem(VisitorCountryService.IP_CACHE_KEY)?.trim();
      if (countryCode && /^[A-Z]{2}$/.test(countryCode)) {
        return { countryCode, ip: ip || null };
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  private storeInfo(info: VisitorGeoInfo): void {
    if (!info.countryCode) {
      return;
    }
    this.cachedInfo = info;
    try {
      sessionStorage.setItem(VisitorCountryService.COUNTRY_CACHE_KEY, info.countryCode);
      if (info.ip) {
        sessionStorage.setItem(VisitorCountryService.IP_CACHE_KEY, info.ip);
      }
    } catch {
      /* ignore quota / private mode */
    }
  }
}
